import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { makeRequestAuth } from "../../../_config/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  HeartPulse,
  AlertCircle,
  Clock,
  ShieldCheck,
  History,
  Calendar,
  FileText,
  Activity,
  ChevronDown,
  Stethoscope
} from "lucide-react";

/* ------------------ CONSTANTS ------------------ */

const EMPTY_FORM = {
  hasSeriousIllness: false,
  hasMentalDisorder: false,
  hasCommunicableDisease: false,
  requiresMedicalTreatment: false,
  medicalDetails: {
    hasSeriousIllness: { date: "", reason: "", result: "" },
    hasMentalDisorder: { date: "", reason: "", result: "" },
    hasCommunicableDisease: { date: "", reason: "", result: "" },
    requiresMedicalTreatment: { date: "", reason: "", result: "" }
  }
};

const STATUS_CONFIG = {
  pending: { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending Review" },
  approve: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <ShieldCheck size={14} />, label: "Approved" },
  reject: { bg: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Rejected / Needs Update" }
};

const MedicalHistoryForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMedicalHistory = async () => {
      setLoading(true);
      try {
        const res = await makeRequestAuth("get", "/user/getMedicalHistory");
        if (res?.data) {
          const apiData = res.data;
          const mappedDetails = { ...EMPTY_FORM.medicalDetails };
          if (Array.isArray(apiData.medicalDetails)) {
            apiData.medicalDetails.forEach((item) => {
              if (mappedDetails[item.type]) {
                mappedDetails[item.type] = {
                  date: item.date ? new Date(item.date).toISOString().split('T')[0] : "",
                  reason: item.reason || "",
                  result: item.result || "",
                };
              }
            });
          }
          setForm({
            hasSeriousIllness: apiData.hasSeriousIllness || false,
            hasMentalDisorder: apiData.hasMentalDisorder || false,
            hasCommunicableDisease: apiData.hasCommunicableDisease || false,
            requiresMedicalTreatment: apiData.requiresMedicalTreatment || false,
            medicalDetails: mappedDetails
          });
          setPreview(apiData);
        }
      } catch (err) {
        console.log("No medical history found");
      } finally {
        setLoading(false);
      }
    };
    fetchMedicalHistory();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetailChange = (type, field, value) => {
    setForm((prev) => ({
      ...prev,
      medicalDetails: { ...prev.medicalDetails, [type]: { ...prev.medicalDetails[type], [field]: value } }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const detailsArray = [];
      Object.keys(form.medicalDetails).forEach((type) => {
        if (form[type]) detailsArray.push({ type, ...form.medicalDetails[type] });
      });

      const payload = {
        hasSeriousIllness: form.hasSeriousIllness,
        hasMentalDisorder: form.hasMentalDisorder,
        hasCommunicableDisease: form.hasCommunicableDisease,
        requiresMedicalTreatment: form.requiresMedicalTreatment,
        medicalDetails: detailsArray
      };

      const res = await makeRequestAuth("post", "/user/createOrUpdateMedicalHistory", payload);
      setPreview(res?.data);
      showSuccessToast("Health declaration submitted successfully");
    } catch (err) {
      const errorMessage = err?.response?.data?.message;

      if (typeof errorMessage === "object" && errorMessage !== null) {
        Object.values(errorMessage).forEach((msg) => {
          showErrorToast(msg);
        });
      } else {
        showErrorToast(errorMessage || "Submission failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 py-6 md:py-12 px-3 sm:px-6">
      <ToastContainer position="top-right" />

      <div className="max-w-4xl mx-auto">
        {/* HEADER */}
        <div className="bg-[#132b45] rounded-t-[1.5rem] md:rounded-t-[2.5rem] p-6 md:p-10 text-white shadow-2xl relative overflow-hidden">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 relative z-10">
            <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
              <div className="bg-white/10 p-4 rounded-2xl backdrop-blur-xl border border-white/20">
                <HeartPulse size={36} className="text-rose-400" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Health Declaration</h1>
                <p className="text-xs text-blue-100/50 uppercase tracking-[0.2em] font-bold mt-1">Medical Records & Fitness Verification</p>
              </div>
            </div>
            {preview?.status && (
              <div className={`px-5 py-2.5 rounded-xl border text-[11px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm ${STATUS_CONFIG[preview.status]?.bg}`}>
                {STATUS_CONFIG[preview.status]?.icon} {STATUS_CONFIG[preview.status]?.label}
              </div>
            )}
          </div>
        </div>

        {/* CONTENT AREA */}
        <div className="bg-white rounded-b-[1.5rem] md:rounded-b-[2.5rem] border-x border-b border-slate-200 p-5 md:p-12 shadow-xl">
          {preview?.status ? (
            /* --- PREVIEW VIEW --- */
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <PreviewCard label="Serious Illness" value={preview.hasSeriousIllness} data={preview.medicalDetails?.find(d => d.type === "hasSeriousIllness")} />
                <PreviewCard label="Mental Disorder" value={preview.hasMentalDisorder} data={preview.medicalDetails?.find(d => d.type === "hasMentalDisorder")} />
                <PreviewCard label="Communicable Disease" value={preview.hasCommunicableDisease} data={preview.medicalDetails?.find(d => d.type === "hasCommunicableDisease")} />
                <PreviewCard label="Ongoing Treatment" value={preview.requiresMedicalTreatment} data={preview.medicalDetails?.find(d => d.type === "requiresMedicalTreatment")} />
              </div>

              {(preview.status === "reject") && (
                <div className="mt-10 pt-8 border-t border-slate-100 flex justify-center">
                  <button onClick={() => setPreview(null)} className="group flex items-center gap-3 px-8 py-4 bg-[#132b45] text-white rounded-2xl hover:bg-slate-800 transition-all font-bold shadow-lg shadow-blue-900/10">
                    <History size={20} className="group-hover:rotate-[-45deg] transition-transform" />
                    Update Information
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* --- FORM VIEW --- */
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100 flex gap-3 items-start mb-8">
                <Stethoscope size={20} className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed font-medium">
                  Please provide accurate information regarding your health history. All declarations are subject to verification.
                </p>
              </div>

              <div className="space-y-10">
                <QuestionBlock label="Do you have any history of serious illness?" value={form.hasSeriousIllness} type="hasSeriousIllness" details={form.medicalDetails.hasSeriousIllness} onChange={handleChange} onDetailChange={handleDetailChange} />
                <QuestionBlock label="Have you ever been diagnosed with a mental disorder?" value={form.hasMentalDisorder} type="hasMentalDisorder" details={form.medicalDetails.hasMentalDisorder} onChange={handleChange} onDetailChange={handleDetailChange} />
                <QuestionBlock label="Do you have any communicable diseases (e.g., Tuberculosis)?" value={form.hasCommunicableDisease} type="hasCommunicableDisease" details={form.medicalDetails.hasCommunicableDisease} onChange={handleChange} onDetailChange={handleDetailChange} />
                <QuestionBlock label="Do you require ongoing medical treatment or supervision?" value={form.requiresMedicalTreatment} type="requiresMedicalTreatment" details={form.medicalDetails.requiresMedicalTreatment} onChange={handleChange} onDetailChange={handleDetailChange} />
              </div>

              <button type="submit" className="w-full bg-[#132b45] text-white py-5 rounded-2xl font-black text-lg shadow-xl hover:shadow-2xl hover:bg-slate-800 transition-all active:scale-[0.98] flex items-center justify-center gap-3 mt-12">
                <Activity size={24} /> Submit Declaration
              </button>
            </form>
          )}
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[100]">
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-[#132b45] rounded-full animate-spin mb-4"></div>
            <p className="text-slate-800 font-bold">Processing Health Data...</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------ REUSABLE FORM BLOCKS ------------------ */

const QuestionBlock = ({ label, value, onChange, type, details, onDetailChange }) => (
  <div className="group animate-in fade-in slide-in-from-bottom-2 duration-500">
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
      <label className="text-base md:text-lg font-bold text-slate-800 max-w-xl">{label}</label>
      <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
        <button type="button" onClick={() => onChange(type, false)} className={`px-6 py-2 rounded-lg text-sm font-black transition-all ${!value ? "bg-white text-slate-800 shadow-sm" : "text-slate-400"}`}>NO</button>
        <button type="button" onClick={() => onChange(type, true)} className={`px-6 py-2 rounded-lg text-sm font-black transition-all ${value ? "bg-rose-500 text-white shadow-md shadow-rose-200" : "text-slate-400"}`}>YES</button>
      </div>
    </div>

    {value && (
      <div className="p-5 md:p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-4">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Date of Occurrence</label>
            <div className="relative">
              <input type="date" value={details.date} onChange={(e) => onDetailChange(type, "date", e.target.value)} className="w-full border-2 border-white rounded-xl px-4 py-3.5 outline-none focus:ring-4 focus:ring-blue-100 bg-white font-bold text-slate-700 shadow-sm transition-all" />
            </div>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Symptoms / Details</label>
              <textarea placeholder="Describe the condition..." rows="3" value={details.reason} onChange={(e) => onDetailChange(type, "reason", e.target.value)} className="w-full border-2 border-white rounded-xl px-4 py-3 outline-none focus:ring-4 focus:ring-blue-100 bg-white resize-none font-medium shadow-sm" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Current Result</label>
              <textarea placeholder="Outcome of treatment..." rows="3" value={details.result} onChange={(e) => onDetailChange(type, "result", e.target.value)} className="w-full border-2 border-white rounded-xl px-4 py-3 outline-none focus:ring-4 focus:ring-blue-100 bg-white resize-none font-medium shadow-sm" />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

/* ------------------ REUSABLE PREVIEW CARD ------------------ */

const PreviewCard = ({ label, value, data }) => {
  return (
    <div className={`relative flex flex-col rounded-[1.5rem] md:rounded-[2rem] border-2 transition-all p-6 ${value ? "bg-rose-50/20 border-rose-100 shadow-sm" : "bg-slate-50/50 border-slate-100"}`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1">{label}</p>
          <p className={`text-xl font-black ${value ? 'text-rose-600' : 'text-emerald-600'}`}>{value ? "Reported" : "No Records"}</p>
        </div>
        <div className={`p-3 rounded-2xl ${value ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white shadow-sm shadow-emerald-100'}`}>
          {value ? <AlertCircle size={22} /> : <ShieldCheck size={22} />}
        </div>
      </div>

      {value && data && (
        <div className="mt-4 pt-5 border-t border-rose-100/50 space-y-4 shrink-0">
          <div className="flex items-center gap-2 bg-white/80 w-fit px-3 py-1.5 rounded-lg border border-rose-100/50">
            <Calendar size={14} className="text-rose-500" />
            <span className="text-xs font-bold text-slate-700">
              {data.date ? new Date(data.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <ScrollableInfo icon={<FileText size={12} />} label="Symptoms" text={data.reason} />
            <ScrollableInfo icon={<Activity size={12} />} label="Treatment Status" text={data.result} />
          </div>
        </div>
      )}

      {!value && (
        <div className="mt-4 flex items-center gap-2 text-[10px] font-bold text-emerald-600 uppercase">
          <ShieldCheck size={12} /> Verification Clear
        </div>
      )}
    </div>
  );
};

const ScrollableInfo = ({ icon, label, text }) => (
  <div className="space-y-1.5">
    <p className="text-[10px] font-black text-rose-400 uppercase flex items-center gap-1.5 px-1 tracking-wider">
      {icon} {label}
    </p>
    <div className="max-h-[80px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-rose-200 scrollbar-track-transparent">
      <p className="text-xs text-slate-600 leading-relaxed break-words whitespace-pre-wrap">
        {text || "N/A"}
      </p>
    </div>
  </div>
);

export default MedicalHistoryForm;